import React, { useState } from 'react';
import { classes } from 'src/utils/styles';
import { useIntl } from 'react-intl';
import styles from './RetailerOpeningHours.module.scss';

import moment from 'moment';
import { Collapse, Row, Col } from 'antd';
const weekDays = {
  en: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  de: ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'],
};

// It
export const formatTime = (value, index = 2, locale: 'en' | 'de') => {
  if (!value) {
    return null;
  }

  let hour = Number(value.substring(0, index));
  let minute = value.substring(index);

  if (locale === 'en') {
    if (hour === 0) {
      hour = 12;
      minute += 'am';
    } else if (hour > 12) {
      hour -= 12;
      minute += 'pm';
    } else {
      minute += 'am';
    }
  }

  return hour + ':' + minute;
};

//Checks if the store is open and returns the closing time if it is, to be displayed in the "Opening time" Collapse header
const getClosingTime = (openingHoursValues, currentLocale: 'en' | 'de') => {
  const now = moment();
  const nowDay = now.day();
  let nowMinute = moment().minute().toString();
  if (nowMinute.length === 1) {
    nowMinute = '0' + nowMinute;
  }
  let nowHour = moment().hour().toString();
  if (nowHour.length === 1) {
    nowHour = '0' + nowHour;
  }
  const nowHourMinute = Number(`${nowHour}${nowMinute}`);
  const storeValue = openingHoursValues.find(
    (value) =>
      value.open.day === nowDay &&
      Number(value.open?.time) <= nowHourMinute &&
      Number(value.close?.time) >= nowHourMinute
  );
  return storeValue?.close ? formatTime(storeValue.close.time, 2, currentLocale) : null;
};

export interface RetailerOpeningHoursType {
  en?: string;
  de?: string;
  values?: {
    close: {
      day: number;
      time: string;
    };
    open: {
      day: number;
      time: string;
    };
  }[];
}

interface RetailerOpeningHoursProps {
  openingHours: RetailerOpeningHoursType;
}

const RetailerOpeningHours: React.FunctionComponent<RetailerOpeningHoursProps> = (
  props: RetailerOpeningHoursProps
) => {
  const intl = useIntl();
  const { openingHours } = props;
  const [openingHoursShown, setOpeningHoursShown] = useState(false);

  let output;

  // Hack to avoid TSC error when not passing collapsible property to Panel. The arrow from the Panel won't close the panel after upgrading Ant design
  const { Panel } = Collapse as any;

  const { values: OpeningHoursValues } = openingHours || {};

  const getOpeningHoursHeader = (OpeningHoursValues, openingHoursShown) => {
    // Check if it's open and get the closing time if it is
    const closingTime = getClosingTime(OpeningHoursValues, currentLocale);
    let output;
    // If the store is open
    if (closingTime) {
      // If Collapse is displayed, show shorter message
      if (openingHoursShown) {
        output = (
          <span className={styles.displayedTime}>
            {intl.formatMessage({ id: 'page/store/opening_times' })}
            {intl.formatMessage({ id: 'page/store/open' })}
          </span>
        );
      } else {
        // Default message when the Collapse is hidden (first load)
        output = (
          <span className={styles.displayedTime}>
            {intl.formatMessage({ id: 'page/store/opening_times' })}
            <span className={styles.open}>
              {intl.formatMessage({ id: 'page/store/open' })}
            </span> ⋅ {intl.formatMessage({ id: 'page/store/closes' })} {closingTime}
          </span>
        );
      }
    } else {
      // Store is closed
      output = (
        <span className={classes([styles.displayedTime, styles.closed])}>
          {intl.formatMessage({ id: 'page/store/opening_times' })}
          {intl.formatMessage({ id: 'page/store/closed' })}
        </span>
      );
    }
    return output;
  };

  const currentLocale = intl.locale === 'en' ? 'en' : 'de';

  // MockDate.set('2020-07-07T09:59:21');
  if (OpeningHoursValues) {
    const _onChange = () => setOpeningHoursShown((prevState) => !prevState);
    const openingHoursHeader = getOpeningHoursHeader(OpeningHoursValues, openingHoursShown);
    const openDays = {};
    output = (
      <div className={styles.container}>
        {/* <p>{moment().format()}</p> */}
        <Collapse
          ghost
          bordered={false}
          // defaultActiveKey={['1']}
          expandIconPosition="right"
          // expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
          onChange={_onChange}
          className="site-collapse-custom-collapse">
          <Panel //tslint:disable-line
            // collapsible="header"
            header={openingHoursHeader}
            key="1"
            className="site-collapse-custom-panel">
            {openingHours.values.map((value, idx) => {
              // Some days have multiple opening times, so ensure only one weekday is printed
              const weekday = openDays[value.open?.day]
                ? null
                : weekDays[currentLocale][value.open?.day];
              openDays[value.open?.day] = true;
              return (
                <Row key={idx} className={styles.openDays}>
                  <Col span={6}>{weekday && <p>{weekDays[currentLocale][value.open.day]}</p>}</Col>
                  <Col span={18}>
                    <p>{`${formatTime(value.open?.time, 2, currentLocale)} - ${formatTime(
                      value.close?.time,
                      2,
                      currentLocale
                    )}`}</p>
                  </Col>
                </Row>
              );
            })}
          </Panel>
        </Collapse>
      </div>
    );
  } else if (openingHours?.en) {
    output = <p className={styles.splitLine}>{openingHours.en}</p>;
  } else {
    output = null;
  }
  return output;
};

export default RetailerOpeningHours;
